import type { DataGridLeafColumn } from '@ibsys/kit';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { PhraseAuditItem } from '@/entities/phrases';
import { useTimeDisplayFormat } from '@/entities/settings';
import { formatHumanDate, formatTimeInterval, withSecondsLabel } from '@/shared/lib';
import { PHRASE_COLUMNS } from '../model/constants';
import { PhraseConversationLink } from './PhraseConversationLink';
import { PhraseTextCell } from './PhraseTextCell';

/** Значения нет — ячейка остаётся пустой */
const EMPTY_CELL = '';

/**
 * Колонки перечня реплик: диалог реплики ссылкой на её место в разговоре, текст с правкой
 * и прослушиванием, продолжительность и время последнего обновления. Отбор — по диалогу,
 * продолжительности и времени обновления, порядок — по двум последним; сам текст
 * не сортируется и не отбирается.
 */
export const usePhraseColumns = (): DataGridLeafColumn<PhraseAuditItem>[] => {
  const { t, i18n } = useTranslation(['audioAudit', 'common']);
  const timeDisplayFormat = useTimeDisplayFormat();

  return useMemo(
    () => [
      {
        id: PHRASE_COLUMNS.conversationId,
        header: t('audioAudit:columns.conversationId'),
        accessor: 'conversationId',
        size: 120,
        enableFiltering: true,
        // Отбор границами идентификатора: одно поле закрывает и точечный случай, и диапазон
        filter: { type: 'integer' },
        cell: ({ row }) => <PhraseConversationLink phrase={row} />,
      },
      {
        id: PHRASE_COLUMNS.text,
        header: t('audioAudit:columns.text'),
        accessor: 'text',
        size: 640,
        wrap: true,
        cell: ({ row }) => <PhraseTextCell phrase={row} />,
      },
      {
        id: PHRASE_COLUMNS.duration,
        header: withSecondsLabel(t('audioAudit:columns.duration'), timeDisplayFormat, t('common:secondsSuffix')),
        accessor: 'duration',
        size: 180,
        enableSorting: true,
        enableFiltering: true,
        // Границы отбора пользователь задаёт в секундах — как и читает продолжительность
        filter: { type: 'integer' },
        cell: ({ row }) =>
          row.duration === null ? EMPTY_CELL : formatTimeInterval(row.duration / 1000, timeDisplayFormat),
      },
      {
        id: PHRASE_COLUMNS.updatedAt,
        header: t('audioAudit:columns.updatedAt'),
        accessor: 'updatedAt',
        size: 200,
        enableSorting: true,
        enableFiltering: true,
        filter: { type: 'timestamp-range' },
        cell: ({ row }) =>
          row.updatedAt === null ? EMPTY_CELL : formatHumanDate(i18n, row.updatedAt, { withTime: true }),
      },
    ],
    [t, i18n, timeDisplayFormat],
  );
};
